export interface EncodingResult {
  success: boolean;
  data?: string;
  error?: string;
}

/**
 * Encode string to Base64
 */
export function encodeBase64(input: string): EncodingResult {
  try {
    // Handle unicode characters
    const encoded = btoa(unescape(encodeURIComponent(input)));
    return { success: true, data: encoded };
  } catch (e) {
    return { success: false, error: 'Failed to encode. Please check your input.' };
  }
}

/**
 * Decode Base64 to string
 */
export function decodeBase64(input: string): EncodingResult {
  try {
    // Handle unicode characters
    const decoded = decodeURIComponent(escape(atob(input)));
    return { success: true, data: decoded };
  } catch (e) {
    return { success: false, error: 'Invalid Base64 string. Please check your input.' };
  }
}

/**
 * Encode URL component
 */
export function encodeUrl(input: string): EncodingResult {
  try {
    const encoded = encodeURIComponent(input);
    return { success: true, data: encoded };
  } catch (e) {
    return { success: false, error: 'Failed to encode URL.' };
  }
}

/**
 * Decode URL component
 */
export function decodeUrl(input: string): EncodingResult {
  try {
    const decoded = decodeURIComponent(input);
    return { success: true, data: decoded };
  } catch (e) {
    return { success: false, error: 'Invalid URL-encoded string.' };
  }
}

/**
 * Encode HTML entities
 */
export function encodeHtmlEntities(input: string): EncodingResult {
  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  
  const encoded = input.replace(/[&<>"']/g, (char) => htmlEntities[char]);
  return { success: true, data: encoded };
}

/**
 * Decode HTML entities
 */
export function decodeHtmlEntities(input: string): EncodingResult {
  const htmlEntities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&#x27;': "'",
    '&#x2F;': '/',
    '&nbsp;': ' ',
  };
  
  // Decode numeric entities
  let decoded = input.replace(/&#(\d+);/g, (_, num) => String.fromCharCode(parseInt(num, 10)));
  decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  
  // Decode named entities
  for (const [entity, char] of Object.entries(htmlEntities)) {
    decoded = decoded.replace(new RegExp(entity, 'g'), char);
  }
  
  return { success: true, data: decoded };
}

/**
 * Encode to hex
 */
export function encodeHex(input: string): EncodingResult {
  try {
    const encoded = Array.from(input)
      .map((char) => char.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('');
    return { success: true, data: encoded };
  } catch (e) {
    return { success: false, error: 'Failed to encode to hex.' };
  }
}

/**
 * Decode from hex
 */
export function decodeHex(input: string): EncodingResult {
  try {
    const cleaned = input.replace(/\s/g, '');
    if (!/^[0-9a-fA-F]*$/.test(cleaned)) {
      return { success: false, error: 'Invalid hex string.' };
    }
    const decoded = cleaned.match(/.{1,2}/g)?.map((byte) => String.fromCharCode(parseInt(byte, 16))).join('') || '';
    return { success: true, data: decoded };
  } catch (e) {
    return { success: false, error: 'Failed to decode from hex.' };
  }
}

/**
 * Encode to binary
 */
export function encodeBinary(input: string): EncodingResult {
  try {
    const encoded = Array.from(input)
      .map((char) => char.charCodeAt(0).toString(2).padStart(8, '0'))
      .join(' ');
    return { success: true, data: encoded };
  } catch (e) {
    return { success: false, error: 'Failed to encode to binary.' };
  }
}

/**
 * Decode from binary
 */
export function decodeBinary(input: string): EncodingResult {
  try {
    const bytes = input.trim().split(/\s+/);
    const decoded = bytes.map((byte) => String.fromCharCode(parseInt(byte, 2))).join('');
    return { success: true, data: decoded };
  } catch (e) {
    return { success: false, error: 'Invalid binary string.' };
  }
}
